<script setup>
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';

// Fields of the ContactDataSchema (es-bill-data-models), edited as one object.
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  // at least first or last name
  requireName: {
    type: Boolean,
    default: false,
  },
  // optional contacts: everything may stay empty, once anything is filled the email is required
  optional: {
    type: Boolean,
    default: false,
  },
  // passed to every field, e.g. { variant: 'outlined', bgColor: 'white' }
  fieldProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const formValidation = inject('$formValidation');

const salutationOptions = ['MR', 'MS', 'NONE'];
const textFields = ['nameFirst', 'nameLast', 'titleBefore', 'titleAfter', 'emailAddress', 'phoneNumber', 'description'];

const contact = computed(() => props.modelValue || {});
const isEmpty = computed(() => textFields.every(field => !(contact.value[field] && String(contact.value[field]).trim())));

function update(field, value) {
  emit('update:modelValue', { ...contact.value, [field]: value ?? '' });
}

const validateNameRequired = () =>
  (contact.value.nameFirst && contact.value.nameFirst.trim()) || (contact.value.nameLast && contact.value.nameLast.trim())
    ? true
    : t('nameRequired');

const validateEmail = value => (props.optional && isEmpty.value) ? true : formValidation.formValidationEmail(value);
</script>

<template>
  <div>
    <v-text-field
      v-bind="fieldProps"
      :label="t('firstName')"
      :model-value="contact.nameFirst"
      @update:model-value="update('nameFirst', $event)"
      :rules="requireName ? [validateNameRequired] : []"
      clearable
    />

    <v-text-field
      v-bind="fieldProps"
      :label="t('lastName')"
      :model-value="contact.nameLast"
      @update:model-value="update('nameLast', $event)"
      :rules="requireName ? [validateNameRequired] : []"
      clearable
    />

    <v-text-field
      v-bind="fieldProps"
      :label="t('titleBefore')"
      :model-value="contact.titleBefore"
      @update:model-value="update('titleBefore', $event)"
      clearable
    />

    <v-text-field
      v-bind="fieldProps"
      :label="t('titleAfter')"
      :model-value="contact.titleAfter"
      @update:model-value="update('titleAfter', $event)"
      clearable
    />

    <v-select
      v-bind="fieldProps"
      :label="t('salutation')"
      :model-value="contact.salutation || 'NONE'"
      @update:model-value="update('salutation', $event)"
      :items="salutationOptions"
    />

    <v-text-field
      v-bind="fieldProps"
      :label="t('emailAddress')"
      :model-value="contact.emailAddress"
      @update:model-value="update('emailAddress', $event)"
      :rules="[validateEmail]"
      clearable
    />

    <v-text-field
      v-bind="fieldProps"
      :label="t('phoneNumber')"
      :model-value="contact.phoneNumber"
      @update:model-value="update('phoneNumber', $event)"
      :rules="[formValidation.formValidPhoneOrEmpty]"
      clearable
    />

    <v-text-field
      v-bind="fieldProps"
      :label="t('description')"
      :model-value="contact.description"
      @update:model-value="update('description', $event)"
      clearable
    />
  </div>
</template>
